// ------------------------------------------------------------------------------
// 初期設定 - ヘッダー項目ヘルパー
// ------------------------------------------------------------------------------
import {HttpStatusCode} from 'axios';
import appConfig from '@/config';

/** ヘッダー名: 認証情報ハッシュ */
export const HEADER_AUTHORIZED = appConfig.authHeader;

/** ヘッダー名: 業務運用状態 */
export const HEADER_RUN_STATUS = appConfig.statHeader;

/**
 * ヘッダー項目値解析
 *
 * @param {AxiosResponseHeaders} headers 応答ヘッダー
 * @param {string} name 解析するヘッダー名
 * @return {HeaderValue|null} ヘッダー項目値 (ヘッダーが存在しない場合はヌル)
 */
export function parseHeader(headers, name) {
  name = name?.toLowerCase() || '';
  if (!Object.hasOwn(headers, name)) {
    return null;
  }

  // ヘッダー値と付加情報を分離
  const header = headers[name];
  const tokens = header.split(';', 2);
  if (!tokens) {
    return new HeaderValue(null);
  }

  // ヘッダー値取得
  const value = tokens[0].trim();
  const result = new HeaderValue(value);
  if (tokens.length === 1) {
    return result;
  }

  // 付加情報解析 - カンマ区切り複数項目
  const options = tokens[1].split(',').map((option) => option.trim());
  for (const option of options) {
    const pos = option.indexOf('=');

    // 項目名=値
    if (pos > 0) {
      const optName = option.substring(0, pos).trimEnd();
      const optValue = option.substring(pos + 1).trimStart();
      switch (optValue) {
        case 'true': result.options[optName] = true; break;
        case 'false': result.options[optName] = false; break;
        default:
          result.options[optName] = optValue;
          break;
      }
      continue;
    }

    // 項目名のみ
    if (pos < 0) {
      result.options[option] = true;
    }
  }
  return result;
};

/**
 * ヘッダー項目値
 *
 * @property {string} value 項目値
 * @property {Object} options 付加情報
 */
export class HeaderValue {
  /**
   * コンストラクタ
   * @constructor
   * @param {string} value ヘッダー値
   */
  constructor(value) {
    this.value = value;
    this.options = {};
  }

  /**
   * 付加情報項目値取得
   *
   * @param {string} name 付加情報項目名
   * @return {string|boolean|null} 付加情報項目値
   */
  option(name) {
    return Object.hasOwn(this.options, name) ? this.options[name] : null;
  }
};

// ------------------------------------------------------------------------------
// ログ出力ヘルパー
// ------------------------------------------------------------------------------
const apiRoot = appConfig.API_URL;

/**
 * ログ出力用URLパス
 *
 * @param {string} url リクエストURL
 * @return {object} ログ出力用に短縮したURL要素
 */
const apiPath = function(url) {
  // クエリーストリングを分離
  const tokens = url ? url.split('?') : [];
  let path = tokens[0];
  const query = tokens[1] ? decodeURIComponent(tokens[1]) : null;

  // URLの先頭固定部を除去
  if (path?.startsWith(apiRoot)) {
    path = path.substring(apiRoot.length);
    path = path.startsWith('/') ? path : '/' + path;
  }
  return {path, query};
};

/**
 * ログ出力用HTTPステータス文字列
 *
 * @param {AxiosResponse} res HTTPレスポンス
 * @return {string} HTTPステータス+ステータス文字列
 */
const statusText = function(res) {
  const code = res.status;
  let text = res.statusText;
  if (!text) {
    text = HttpStatusCode[code]
        ?.split(/(?=[A-Z])/)
        ?.join(' ');
  }
  return code + ' (' + text + ')';
};

// ------------------------------------------------------------------------------
// エクスポート
// ------------------------------------------------------------------------------
export default {
  HEADER_AUTHORIZED,
  HEADER_RUN_STATUS,
  parseHeader,
  HeaderValue,

  // ログ出力ヘルパー
  statusText,
  apiPath,
};
